import { usePortfolio } from '../context/PortfolioContext';
import { motion } from 'framer-motion';
import * as FaIcons from 'react-icons/fa';
import { useState } from 'react';

const About = () => {
  const { portfolioData } = usePortfolio();
  const about = portfolioData?.about;
  const roleItems = about?.roles || [];
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = () => setIsExpanded((prev) => !prev);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const childVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <section id="about" className="py-20 bg-gray-900 min-h-screen">
      <div className="container mx-auto px-4">
        <motion.div 
          className="flex items-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <FaIcons.FaUserAlt className="text-primary text-2xl" />
          <h2 className="section-title text-white">About Me</h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left column: Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="w-full max-w-xs mx-auto aspect-square rounded-lg overflow-hidden relative">
              <img
                src={about?.image_url || '/about/about.jpg'}
                alt={about?.title || 'about'}
                className="w-full h-full object-cover rounded-xl border-2 border-primary shadow-lg"
                style={{ background: 'linear-gradient(135deg, #232526 0%, #414345 100%)' }}
              />
              <div className="absolute inset-0 pointer-events-none rounded-xl" style={{ background: 'linear-gradient(135deg,rgba(0,0,0,0.05) 0%,rgba(0,0,0,0.15) 100%)' }} />
              {about?.tagline && (
            <motion.div 
                  className="absolute bottom-4 right-4 flex items-center gap-3 px-7 py-3 rounded-2xl shadow-2xl backdrop-blur-xl bg-white/60 border border-white/30 z-10"
                  initial={{ scale: 0.95, opacity: 0, y: 10, rotate: -2 }}
                  whileInView={{ scale: 1, opacity: 1, y: 0, rotate: 0 }}
              animate={{
                    y: [0, -8, 0],
                    rotate: [-2, 2, -2],
              }}
              transition={{
                y: {
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut"
                },
                    rotate: {
                      duration: 6,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut"
                }
              }}
              viewport={{ once: true }}
                  style={{ minWidth: 'fit-content', maxWidth: '80%' }}
                >
                  {/* Glow effect */}
                  <div className="absolute -inset-2 -z-10 rounded-2xl bg-primary/30 blur-2xl opacity-60" />
                  {/* Animated accent bar */}
                  <motion.span
                    className="w-2 h-8 rounded-full bg-primary block"
                  animate={{
                      scaleY: [1, 1.2, 1],
                      opacity: [0.8, 1, 0.8]
                  }}
                  transition={{
                      duration: 1.5,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "easeInOut"
                  }}
                />
                  <span className="text-primary font-extrabold text-lg tracking-wide" style={{ letterSpacing: '0.04em', textShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>{about.tagline}</span>
                </motion.div>
              )}
              </div>
          </motion.div>

          {/* Right column: Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.h3 
              className="text-4xl font-extrabold text-white mb-2 tracking-tight relative"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {about?.title || 'No title set'}
              <span className="block w-16 h-1 bg-primary rounded-full mt-2 absolute left-0 bottom-[-10px]" />
            </motion.h3>
            <motion.p 
              className="text-gray-300 mb-8 whitespace-pre-line text-lg leading-relaxed drop-cap"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              {about?.content || 'No about content set. Please add it in the admin dashboard.'}
            </motion.p>

            <div className="overflow-hidden relative w-full mb-8">
              <motion.div
                className="flex gap-6 w-max"
                initial={{ x: 0 }}
                animate={{ x: ['0%', '-50%'] }}
                transition={{
                  duration: 25,
                  ease: 'linear',
                  repeat: Infinity
                }}
              >
                {[...roleItems, ...roleItems].map((role, index) => {
                  const IconComponent = role.icon && FaIcons[role.icon] ? FaIcons[role.icon] : null;
                  return (
                    <motion.div
                      key={index}
                      className="flex-shrink-0 w-80 bg-gradient-to-br from-gray-800/80 to-primary/10 rounded-xl shadow-lg p-6 group hover:scale-[1.03] hover:shadow-2xl transition-all border-l-4 border-primary"
                    >
                      {IconComponent && (
                        <div className="p-4 bg-primary/20 rounded-full group-hover:bg-primary transition-colors flex items-center justify-center shadow-lg">
                          <IconComponent className="text-primary text-3xl group-hover:text-white transition-colors" />
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-white text-xl mb-1 tracking-wide">{role.title}</div>
                        <div className="text-gray-300 text-base">{role.description}</div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>

            <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[1000px]' : 'max-h-0'}`}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={isExpanded ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                {about?.extra_content && (
                  <p className="text-white text-lg mb-6 whitespace-pre-line">
                    {about.extra_content}
                  </p>
                )}
              </motion.div>
            </div>

            <motion.button
              className="flex items-center gap-2 mt-6 px-6 py-2 bg-gradient-to-r from-primary to-secondary text-white font-semibold rounded-full shadow-lg hover:scale-105 transition-all"
              onClick={toggleExpand}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.97 }}
            >
              {isExpanded ? (
                <>
                  Read Less <FaIcons.FaChevronUp className="text-sm" />
                </>
              ) : (
                <>
                  Read More <FaIcons.FaChevronDown className="text-sm" />
                </>
              )}
            </motion.button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About; 